/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { createContext, useState, useContext, useEffect, useRef } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db, realtimeDb } from "../firebaseFunctions/config";
import { ref, onValue } from "firebase/database";
import { authService } from "./AuthService";
import { getDoc, doc } from "firebase/firestore";
import { referralService } from "../services/ReferralService";
import { subscriptionService } from "../services/SubscriptionService";
import DOMAIN_CONFIG from "../config/domains";

const AuthContext = createContext();

const generateSessionId = () => {
  return (
    Math.random()
      .toString(36)
      .substring(2) + Date.now().toString(36)
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem("userType") || null;
  });
  const [isSignedUp, setIsSignedUp] = useState(() => {
    // Initialize from localStorage
    return localStorage.getItem("isSignedUp") || null;
  });
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(true);
  const [initialAuthCheckDone, setInitialAuthCheckDone] = useState(false);
  const [stripeConnected, setStripeConnected] = useState(null);
  const [stripeUserId, setStripeUserId] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState(() => {
    return localStorage.getItem("subscriptionTier") || 'free';
  });
  const [hasActiveSubscription, setHasActiveSubscription] = useState(() => {
    const stored = localStorage.getItem("hasActiveSubscription");
    return stored === null ? false : stored === 'true';
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => {
    return localStorage.getItem("subscriptionStatus") || 'none';
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  const updateUserType = (type) => {
    setUserType(type);
    if (type) {
      localStorage.setItem("userType", type);
    } else {
      localStorage.removeItem("userType");
    }
  };
  const updateUserSignedUp = (type) => {
    setIsSignedUp(type);
    if (type) {
      localStorage.setItem("isSignedUp", type);
    } else {
      localStorage.removeItem("isSignedUp");
    }
  };

  const updateSubscription = (subscriptionData) => {
    const { hasActive, status, tier } = subscriptionData;

    setHasActiveSubscription(hasActive);
    setSubscriptionStatus(status);
    if (tier) setSubscriptionTier(tier);

    localStorage.setItem("hasActiveSubscription", String(hasActive));
    localStorage.setItem("subscriptionStatus", status);
    if (tier) localStorage.setItem("subscriptionTier", tier);
  };

  const checkSubscriptionStatus = async (userId) => {
    if (!userId) return;

    try {
      const result = await subscriptionService.getSubscriptionStatus(userId);
      updateSubscription({
        hasActive: result.hasActiveSubscription,
        status: result.status,
        tier: result.plan || 'free'
      });
      return result;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return { hasActiveSubscription: false, status: 'error' };
    }
  };

  // Track if we're doing cross-domain auth
  // Capture initial token in a ref to survive React StrictMode double-mounting
  const initialAuthTokenRef = useRef(DOMAIN_CONFIG.getAuthTokenFromUrl());
  const hasHandledCrossDomainAuthRef = useRef(false);

  const [crossDomainAuthPending, setCrossDomainAuthPending] = useState(() => {
    // Check immediately if there's a token in URL
    return !!initialAuthTokenRef.current;
  });

  // Handle cross-domain auth token when arriving from auth domain
  useEffect(() => {
    const handleCrossDomainAuth = async () => {
      // Use the ref to get the original token (survives StrictMode double-mount)
      const authToken = initialAuthTokenRef.current;

      // Only run once - prevent StrictMode double-execution
      if (hasHandledCrossDomainAuthRef.current) {
        return;
      }

      if (!authToken) {
        setCrossDomainAuthPending(false);
        return;
      }

      // Mark as handled immediately to prevent double-execution
      hasHandledCrossDomainAuthRef.current = true;

      console.log('Cross-domain auth: token found, exchanging...');

      try {
        const exchangeResult = await authService.exchangeTokenForCustomToken(authToken);

        if (exchangeResult.success) {
          const signInResult = await authService.signInWithToken(exchangeResult.customToken);

          if (signInResult.success) {
            console.log('Cross-domain auth: signed in successfully, waiting for onAuthStateChanged...');
            DOMAIN_CONFIG.clearAuthTokenFromUrl();
            // DON'T set crossDomainAuthPending = false here!
            // Wait for onAuthStateChanged to fire with the user first
          } else {
            console.error('Cross-domain auth: sign in failed', signInResult.message);
            DOMAIN_CONFIG.clearAuthTokenFromUrl();
            setCrossDomainAuthPending(false);
          }
        } else {
          console.error('Cross-domain auth: token exchange failed', exchangeResult.message);
          DOMAIN_CONFIG.clearAuthTokenFromUrl();
          setCrossDomainAuthPending(false);
        }
      } catch (error) {
        console.error('Cross-domain auth error:', error);
        DOMAIN_CONFIG.clearAuthTokenFromUrl();
        setCrossDomainAuthPending(false);
      }
    };

    handleCrossDomainAuth();
  }, []);

  // Handle Google redirect result when app loads
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        const result = await authService.handleRedirectResult();

        if (result.noRedirect) {
          // No redirect happened, continue normally
          return;
        }

        if (result.success) {
          // User successfully signed in via Google redirect
          const sessionId =
            window.sessionStorage.getItem("sessionId") || generateSessionId();
          window.sessionStorage.setItem("sessionId", sessionId);

          await authService.updateSessionUserType(
            result.user.uid,
            sessionId,
            result.userType
          );

          setUser(result.user);
          // Use helper functions to persist to localStorage
          updateUserType(result.userType);
          updateUserSignedUp(result.isSignedUp);
        } else if (result.message) {
          // Show error to user - improved messaging
          console.error("Google redirect error:", result.message);
          alert(result.message || "Authentication failed. Please try again.");
          // Redirect to login on error
          if (typeof window !== 'undefined') {
            setTimeout(() => {
              window.location.href = '/login';
            }, 1500);
          }
        }
      } catch (error) {
        console.error("Error handling redirect:", error);
        // Handle unexpected errors gracefully
        alert("An unexpected error occurred. Please try again.");
        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.href = '/login';
          }, 1500);
        }
      }
    };

    handleRedirect();
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setLoading(true);
        if (user) {
          // Cross-domain auth is complete - we have the user now
          if (crossDomainAuthPending) {
            console.log('Cross-domain auth: user confirmed via onAuthStateChanged');
            setCrossDomainAuthPending(false);
          }

          // Fetch user data to verify
          await authService.getUserData(user.uid);
          setUser(user);

          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);

          if (userDocSnap.exists()) {
            const userDocData = userDocSnap.data();
            setStripeConnected(userDocData.stripeConnected || false);
            setStripeUserId(userDocData.stripeUserId || null);

            // Set subscription tier (defaults to 'basic' if not set)
            const tier = userDocData.subscriptionTier || 'basic';
            setSubscriptionTier(tier);
            localStorage.setItem("subscriptionTier", tier);

            // Set isSignedUp based on user type
            // Use same logic as checkExistingUser: profile OR signedUp flag = true
            if (userDocData.signedInAs === 'EO') {
              const eoIsSignedUp = userDocData.isEoSignedUp || userDocData.eoProfile || false;
              setIsSignedUp(eoIsSignedUp);
              setUserType('EO');
              localStorage.setItem("isSignedUp", String(eoIsSignedUp));

              // Check subscription status for EO users
              const subStatus = await subscriptionService.getSubscriptionStatus(user.uid);
              updateSubscription({
                hasActive: subStatus.hasActiveSubscription,
                status: subStatus.status,
                tier: subStatus.plan || 'free'
              });
            } else if (userDocData.signedInAs === 'MPS') {
              // If user has mpsProfile, they're signed up (fixes legacy accounts)
              const mpsIsSignedUp = userDocData.isMpsSignedUp || userDocData.mpsProfile || false;
              setIsSignedUp(mpsIsSignedUp);
              setUserType('MPS');
              localStorage.setItem("isSignedUp", String(mpsIsSignedUp));
              // MPS users don't need subscription
              updateSubscription({
                hasActive: true,
                status: 'not_required',
                tier: null
              });
            } else {
              // signedInAs is missing or invalid - fall back to localStorage
              const storedUserType = localStorage.getItem("userType");
              const storedIsSignedUp = localStorage.getItem("isSignedUp") === 'true';
              if (storedUserType) {
                setUserType(storedUserType);
                setIsSignedUp(storedIsSignedUp);
                if (storedUserType === 'MPS') {
                  updateSubscription({
                    hasActive: true,
                    status: 'not_required',
                    tier: null
                  });
                }
              }
            }
          } else {
            // No Firestore document exists yet - this happens during Google sign-in
            // before the profile is created. Preserve localStorage values to avoid race condition.
            console.log("No user document found - preserving localStorage state while waiting");
            const storedUserType = localStorage.getItem("userType");
            const storedIsSignedUp = localStorage.getItem("isSignedUp") === 'true';

            // Only preserve if we have valid localStorage values (set by signIn/signInWithGoogle)
            if (storedUserType) {
              setUserType(storedUserType);
              setIsSignedUp(storedIsSignedUp);
              // Set appropriate subscription status based on user type
              if (storedUserType === 'MPS') {
                updateSubscription({
                  hasActive: true,
                  status: 'not_required',
                  tier: null
                });
              }
            } else {
              // No localStorage values - truly new user, wait for profile creation
              setStripeConnected(null);
              setStripeUserId(null);
            }
          }

          const sessionId =
            window.sessionStorage.getItem("sessionId") || generateSessionId();
          window.sessionStorage.setItem("sessionId", sessionId);

          const sessionRef = ref(
            realtimeDb,
            `userSessions/${user.uid}/${sessionId}`
          );
          onValue(sessionRef, (snapshot) => {
            const sessionData = snapshot.val();
            if (sessionData) {
              updateUserType(sessionData.userType);
            }
          });
        } else {
          setUser(null);
          setUserType(null);
          setIsSignedUp(false);
        }
      } catch (error) {
        console.error("Auth state change error:", error);
      } finally {
        setLoading(false);
        setInitialAuthCheckDone(true);
      }
    });

    return () => unsubscribe();
  }, []);

  const signUp = async (email, password, userType, referralData = null) => {
    localStorage.clear();
    const result = await authService.signUp(email, password, userType);
    if (result.success) {
      if (referralData && referralData.referralCode && referralData.referrerId) {
        try {
          await referralService.storeReferralAtSignup(
            result.user.uid,
            referralData.referralCode,
            referralData.referrerId
          );
        } catch (error) {
          console.error("Error storing referral data:", error);
        }
      }

      if (result.emailVerificationSent) {
        return result;
      }

      const sessionId =
        window.sessionStorage.getItem("sessionId") || generateSessionId();
      window.sessionStorage.setItem("sessionId", sessionId);

      await authService.updateSessionUserType(
        result.user.uid,
        sessionId,
        userType
      );

      setUser(result.user);
      setUserType(userType);
      setIsSignedUp(false);
    } else {
      alert(result.message);
    }
    return result;
  };

  const setUserSignedUp = async (isSignedUp, explicitUserType = null) => {
    if (!user) return { success: false, message: "User not authenticated" };
    const typeToUse = explicitUserType || userType;
    if (!typeToUse) return { success: false, message: "User type is required" };

    const result = await authService.setUserSignedUp(
      user.uid,
      typeToUse,
      isSignedUp
    );
    if (result.success) {
      setIsSignedUp(isSignedUp);
      if (explicitUserType) {
        setUserType(explicitUserType);
        localStorage.setItem("userType", explicitUserType);
      }
    }
    return result;
  };

  const signIn = async (email, password, userType) => {
    localStorage.clear();
    const result = await authService.signIn(email, password, userType);
    if (result.success) {
      const sessionId =
        window.sessionStorage.getItem("sessionId") || generateSessionId();
      window.sessionStorage.setItem("sessionId", sessionId);
      console.log("creating session");
      await authService.updateSessionUserType(
        result.user.uid,
        sessionId,
        userType
      );

      setUser(result.user);
      updateUserType(userType);
      updateUserSignedUp(result.isSignedUp);

      // Load subscription status immediately for EO users to prevent redirect race condition
      if (userType === 'EO') {
        setSubscriptionLoading(true);
        try {
          const subStatus = await subscriptionService.getSubscriptionStatus(result.user.uid);
          updateSubscription({
            hasActive: subStatus.hasActiveSubscription,
            status: subStatus.status,
            tier: subStatus.plan || 'free'
          });
        } catch (error) {
          console.error('Error loading subscription during signIn:', error);
        } finally {
          setSubscriptionLoading(false);
        }
      } else {
        // MPS users don't need subscription
        updateSubscription({
          hasActive: true,
          status: 'not_required',
          tier: null
        });
      }
    }
    return result;
  };

  const signInWithGoogle = async (userType) => {
    localStorage.clear();
    const result = await authService.signInWithGoogle(userType);

    if (result.success) {
      // Check if this is a redirect flow (user will be redirected to Google)
      if (result.redirecting) {
        // Don't try to access result.user - redirect will happen immediately
        return result;
      }

      // Legacy: immediate popup result (fallback, shouldn't happen with redirect flow)
      const sessionId =
        window.sessionStorage.getItem("sessionId") || generateSessionId();
      window.sessionStorage.setItem("sessionId", sessionId);

      await authService.updateSessionUserType(
        result.user.uid,
        sessionId,
        userType
      );

      setUser(result.user);
      updateUserType(userType);
      updateUserSignedUp(result.isSignedUp);

      // Load subscription status immediately to prevent redirect race condition
      if (userType === 'EO') {
        setSubscriptionLoading(true);
        try {
          const subStatus = await subscriptionService.getSubscriptionStatus(result.user.uid);
          updateSubscription({
            hasActive: subStatus.hasActiveSubscription,
            status: subStatus.status,
            tier: subStatus.plan || 'free'
          });
        } catch (error) {
          console.error('Error loading subscription during Google signIn:', error);
        } finally {
          setSubscriptionLoading(false);
        }
      } else {
        updateSubscription({
          hasActive: true,
          status: 'not_required',
          tier: null
        });
      }
    } else {
      auth.signOut();
    }
    return result;
  };

  const signUpWithGoogle = async (userType) => {
    localStorage.clear();
    const result = await authService.signUpWithGoogle(userType);

    if (result.success) {
      // Check if this is a redirect flow (user will be redirected to Google)
      if (result.redirecting) {
        // Don't try to access result.user - redirect will happen immediately
        return result;
      }

      // Legacy: immediate popup result (fallback, shouldn't happen with redirect flow)
      const sessionId =
        window.sessionStorage.getItem("sessionId") || generateSessionId();
      window.sessionStorage.setItem("sessionId", sessionId);

      await authService.updateSessionUserType(
        result.user.uid,
        sessionId,
        userType
      );

      setUser(result.user);
      updateUserType(userType);
      updateUserSignedUp(result.isSignedUp);
    } else {
      auth.signOut();
    }
    return result;
  };
  const signOut = async () => {
    try {
      const sessionId = window.sessionStorage.getItem("sessionId");
      if (user && sessionId) {
        await authService.clearSession(user.uid, sessionId);
      }
      window.sessionStorage.removeItem("sessionId");

      // Clear all state to prevent stale data for next user
      setHasActiveSubscription(false);
      setSubscriptionStatus('none');
      setSubscriptionTier('free');
      setUserType(null);
      setUser(null);
      setStripeConnected(null);
      setStripeUserId(null);
      setIsSignedUp(false);

      // Clear ALL localStorage to prevent auto-login on reload
      localStorage.clear();

      await auth.signOut();

      // Redirect to auth domain after signOut completes
      DOMAIN_CONFIG.redirectToAuth('/login');
    } catch (error) {
      console.error("Error signing out:", error);
      // Still clear localStorage even on error
      localStorage.clear();
      // Still redirect even on error to prevent user from being stuck
      DOMAIN_CONFIG.redirectToAuth('/login');
    }
  };

  if (!initialAuthCheckDone) {
    return null;
  }

  const value = {
    user,
    userType,
    isSignedUp,
    stripeConnected,
    stripeUserId,
    subscriptionTier,
    hasActiveSubscription,
    subscriptionStatus,
    subscriptionLoading,
    loading,
    initialAuthCheckDone,
    crossDomainAuthPending,
    signUp,
    signIn,
    signInWithGoogle,
    setIsSignedUp,
    setUserType,
    setUserSignedUp,
    signUpWithGoogle,
    signOut,
    updateSubscription,
    checkSubscriptionStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  return context;
};

// Export the raw context for external use
export { AuthContext };
